<script>
import VueSelect from 'vue-select';
import { debounce } from 'lodash';
import Competencies from './Competencies.vue';
import EmploymentPhases from './EmploymentPhases.vue';
import BackgroundVerificationSpecs from './BackgroundVerifications.vue';
import SkillAssessmentSpecs from './SkillsAssessments.vue';
import KillerQuestionSpecs from './KillerQuestions.vue';
import InterviewQuestions from './InterviewQuestions.vue';
import RequiredDocuments from './RequiredDocuments.vue';
import RedactorControl from '../RedactorControl.vue';
import FileInputControl from '../FileInputControl.vue';

export default {
    components: {
        VueSelect,
        Competencies,
        EmploymentPhases,
        BackgroundVerificationSpecs,
        SkillAssessmentSpecs,
        KillerQuestionSpecs,
        InterviewQuestions,
        RequiredDocuments,
        RedactorControl,
        FileInputControl
    },

    props: {
        personSpec: {
            type: Object,
            required: true
        },
        isUsedByRequisition: {
            type: Boolean,
            required: true
        },
        clientBriefLink: {
            required: true
        },
        csrfToken: {
            type: String,
            required: true
        },
        knowledgeCompetencies: {
            type: Array,
            required: true
        },
        attributeCompetencies: {
            type: Array,
            required: true
        },
        skillCompetencies: {
            type: Array,
            required: true
        },
        experienceCompetencies: {
            type: Array,
            required: true
        },
        classmarkerAssessments: {
            type: Array,
            required: true
        },
        iFactsChecks: {
            type: Array,
            required: true
        },
        baselines: {
            type: Array,
            required: true
        },
        languages: {
            type: Array,
            required: true
        },
        qualificationLevels: {
            type: Array,
            required: true
        },
        errors: {
            type: Array,
            required: true
        },
        atsTypes: {
            type: Object,
            required: true
        },
    },

    data() {
        return {
            company_id: this.personSpec.business_line ? this.personSpec.business_line.company_id : null,
            companies: this.personSpec.business_line ? [this.personSpec.business_line.company] : [],
            company: this.personSpec.business_line ? this.personSpec.business_line.company : null,
            businessLines: this.personSpec.business_line ? [this.personSpec.business_line] : [],
            businessLineId: this.personSpec.business_line_id || null,
            baseline: this.personSpec.baselines || null,
            baselineId: this.personSpec.baseline_id || null,
            selectedLanguages: this.personSpec.person_specification_languages ? this.personSpec.person_specification_languages.map(comp => comp.language_id) : [],
            atsType: this.personSpec.ats_type
        }
    },

    methods: {
        fetchCompanies(search, loading) {
            if (search.length) {
                loading(true);
                this.search(loading, `/admin/companies.json?search=${search}&limit=1000`, this);
            }
        },
        search: debounce((loading, url, vm) => {
            fetch(url).then(res => {
                res.json().then(json => (vm.companies = json.companies));
                loading(false);
            });
        }, 250)
    }
}
</script>

<template>
    <form class="person-spec-crud" method="POST" enctype="multipart/form-data">
        <div v-if="errors.length > 0" class="errors mb-4">
            <ul>
                <li v-for="error in errors">
                    {{ error }}
                </li>
            </ul>
        </div>
        <fieldset>
            <div class="grid-x grid-padding-x mb-2">
                <div class="cell medium-6 large-5">
                    <fieldset>
                        <legend>ATS Type</legend>
                        <select class="personspec-dropdown" :disabled="isUsedByRequisition" name="ats_type" v-model="atsType">
                            <option value="agent">Agent</option>
                            <option value="leadership_and_support">Leadership And Support</option>
                        </select>
                    </fieldset>
                </div>
            </div>

            <div class="grid-x grid-padding-x">
                <div class="cell medium-6 large-5">
                    <label>Person Specification Title</label>

                    <input :disabled="isUsedByRequisition" name="title" type="text" required v-model="personSpec.title" />
                </div>
            </div>

            <div class="grid-x grid-padding-x mb-4">
                <div class="cell medium-6 large-5">
                    <label>Business Unit</label>

                    <vue-select
                        class="personspec-dropdown"
                        :disabled="isUsedByRequisition"
                        label="title"
                        @search="fetchCompanies"
                        @input="company => {
                            businessLineId = null;
                            businessLines = company.business_lines;
                        }"
                        :options="companies"
                        v-model="company"
                    >
                        <template slot="option" slot-scope="option">
                            {{ option.title }}
                        </template>
                    </vue-select>
                </div>
            </div>

            <div class="grid-x grid-padding-x mb-4">
                <div class="cell medium-6 large-5">
                    <label>Line of Business</label>

                    <input :disabled="isUsedByRequisition" name="business_line_id" type="hidden" required :value="businessLineId" />

                    <vue-select
                        class="personspec-dropdown"
                        :disabled="isUsedByRequisition"
                        label="title"
                        :reduce="businessLine => businessLine.id"
                        :options="businessLines"
                        v-model="businessLineId"
                    />
                </div>
            </div>
            <div class="grid-x grid-padding-x mb-4">
                <div class="cell medium-6 large-5">
                    <label>Base Lines</label>

                    <input :disabled="isUsedByRequisition" name="baseline_id" type="hidden" required :value="baselineId" />

                    <vue-select
                        class="personspec-dropdown"
                        :disabled="isUsedByRequisition"
                        label="area" :reduce="baseline => baseline.id"
                        :options="baselines"
                        v-model="baselineId"
                    />
                </div>
            </div>

            <div class="grid-x grid-padding-x">
                <div class="cell medium-6 large-5">
                    <label>Purpose of Role</label>

                    <textarea v-if="isUsedByRequisition" :disabled="isUsedByRequisition" name="purpose" type="text" required v-model="personSpec.purpose" rows="5"></textarea>

                    <RedactorControl v-if="!isUsedByRequisition"
                        name="purpose"
                        :value="(personSpec) ? personSpec.purpose : ''"
                        rows="5"
                    />
                </div>
            </div>
        </fieldset>

        <fieldset>
            <legend>Competencies</legend>

            <Competencies
                :disabled="isUsedByRequisition"
                type="knowledge"
                title="Knowledge & Education"
                :competencies="knowledgeCompetencies"
                :selected-preferred-ids="personSpec.preferred_knowledge_competencies ? personSpec.preferred_knowledge_competencies.map(comp => comp.id) : []"
                :selected-essential-ids="personSpec.essential_knowledge_competencies ? personSpec.essential_knowledge_competencies.map(comp => comp.id) : []"
            />

            <Competencies
                :disabled="isUsedByRequisition"
                type="experience"
                title="Experience"
                :competencies="experienceCompetencies"
                :selected-preferred-ids="personSpec.preferred_experience_competencies ? personSpec.preferred_experience_competencies.map(comp => comp.id) : []"
                :selected-essential-ids="personSpec.essential_experience_competencies ? personSpec.essential_experience_competencies.map(comp => comp.id) : []"
            />

            <Competencies
                :disabled="isUsedByRequisition"
                type="skill"
                title="Skill"
                :competencies="skillCompetencies"
                :selected-preferred-ids="personSpec.preferred_skill_competencies ? personSpec.preferred_skill_competencies.map(comp => comp.id) : []"
                :selected-essential-ids="personSpec.essential_skill_competencies ? personSpec.essential_skill_competencies.map(comp => comp.id) : []"
            />

            <Competencies
                :disabled="isUsedByRequisition"
                type="attribute"
                title="Attribute"
                :competencies="attributeCompetencies"
                :selected-preferred-ids="personSpec.preferred_attribute_competencies ? personSpec.preferred_attribute_competencies.map(comp => comp.id) : []"
                :selected-essential-ids="personSpec.essential_attribute_competencies ? personSpec.essential_attribute_competencies.map(comp => comp.id) : []"
            />
        </fieldset>

        <fieldset>
            <legend>Languages</legend>
            <div class="grid-x grid-padding-x">
                <div class="cell medium-6 large-5">
                    <label>Languages</label>

                    <vue-select
                        class="personspec-dropdown"
                        :disabled="isUsedByRequisition"
                        label="title"
                        :reduce="language => language.id"
                        :options="languages"
                        v-model="selectedLanguages"
                        :multiple="true"
                    />

                    <template v-if="selectedLanguages.length > 0">
                        <template v-for="(id, index) in selectedLanguages">
                            <input :name="`person_specification_languages[${index}][language_id]`" :value="id" type="hidden" />
                        </template>
                    </template>
                    <input v-else name="person_specification_languages" value="" type="hidden" />
                </div>
            </div>
        </fieldset>

        <fieldset>
            <legend>Shift Info</legend>

            <div class="grid-x grid-padding-x mb-2">
                <div class="cell medium-6 large-5">
                    <label>Shift Type</label>

                    <select class="personspec-dropdown" :disabled="isUsedByRequisition" name="shift_type" v-model="personSpec.shift_type">
                        <option value="day">Day Shift</option>
                        <option value="night">Night Shift</option>
                    </select>
                </div>

                <div class="cell medium-6 large-5">
                    <label>Shift Service Area</label>

                    <select class="personspec-dropdown" :disabled="isUsedByRequisition" name="shift_service_area" v-model="personSpec.shift_service_area">
                        <option value="America">America</option>
                        <option value="Australia">Australia</option>
                        <option value="Canada">Canada</option>
                        <option value="South Africa">South Africa</option>
                        <option value="UK">UK</option>
                    </select>
                </div>
            </div>

            <div class="grid-x grid-padding-x mb-2">
                <div class="cell medium-6 large-5">
                    <label>Shift Hours</label>

                    <textarea v-if="isUsedByRequisition" :disabled="isUsedByRequisition" name="shift_hours" type="text" v-model="personSpec.shift_hours" rows="5"></textarea>

                    <RedactorControl v-if="!isUsedByRequisition"
                        name="shift_hours"
                        :value="(personSpec) ? personSpec.shift_hours : ''"
                        rows="5"
                    />
                </div>

                <div class="cell medium-6 large-5">
                    <label>Breaks & Lunch</label>
                    
                    <textarea v-if="isUsedByRequisition" :disabled="isUsedByRequisition" name="breaks_and_lunch" type="text" v-model="personSpec.breaks_and_lunch" rows="5"></textarea>
                    
                    <RedactorControl v-if="!isUsedByRequisition"
                        name="breaks_and_lunch"
                        :value="(personSpec) ? personSpec.breaks_and_lunch : ''"
                        rows="5"
                    />
                </div>
            </div>
        </fieldset>

        <div class="grid-x grid-padding-x mb-2">
            <div class="cell medium-6 large-5">
                <fieldset>
                    <legend>Employment Phases</legend>
                    <employment-phases :disabled="isUsedByRequisition" :default-employment-phases="personSpec.employment_phase_specs || []" />
                </fieldset>
            </div>
        </div>

        <div class="grid-x grid-padding-x mb-2">
            <div class="cell medium-6 large-5">
                <fieldset>
                    <legend>Background Verification</legend>

                    <background-verification-specs
                        :disabled="isUsedByRequisition"
                        :default-background-verification-specs="personSpec.background_verification_specs || []"
                        :i-facts-checks="iFactsChecks"
                    />
                </fieldset>
            </div>
        </div>
        <div class="grid-x grid-padding-x mb-2">
            <div class="cell medium-6 large-10">
                <fieldset>
                    <legend>Skills Assessments</legend>

                    <skill-assessment-specs
                        :disabled="isUsedByRequisition"
                        :default-skill-assessment-specs="personSpec.skill_assessment_specs || []"
                        :classmarker-assessments="classmarkerAssessments"
                    />
                </fieldset>
            </div>
        </div>

        <div class="grid-x grid-padding-x mb-2">
            <div class="cell medium-6 large-5">
                <fieldset>
                    <legend>Killer Questions</legend>

                    <killer-question-specs :disabled="isUsedByRequisition" :default-killer-question-specs="personSpec.killer_questions || []" />
                </fieldset>
            </div>
        </div>
        <div class="grid-x grid-padding-x mb-2">
            <div class="cell medium-6 large-5">
                <fieldset>
                    <legend>Client Original Brief</legend>
                        <label v-if="clientBriefLink">
                            <small v-if="clientBriefLink">(Existing: <a target="_blank" :href="clientBriefLink">{{ clientBriefLink }}</a>)</small>
                        </label>

                        <FileInputControl name="client_original_brief" :disabled="isUsedByRequisition"/>
                </fieldset>
            </div>
        </div>
        <div class="grid-x grid-padding-x mb-2">
            <div class="cell medium-6 large-5">
                <fieldset>
                    <legend>Interview Questions</legend>

                    <interview-questions :disabled="isUsedByRequisition" :default-interview-questions="personSpec.interview_questions || []" />
                </fieldset>
            </div>
        </div>
        <div class="grid-x grid-padding-x mb-2">
            <div class="cell medium-6 large-5">
                <fieldset>
                    <legend>Required Documents</legend>

                    <required-documents
                        :disabled="isUsedByRequisition"
                        :default-required-documents="personSpec.required_documents || []"
                        :qualification-levels="qualificationLevels"
                    />
                </fieldset>
            </div>
        </div>

        <div v-if="atsType == 'leadership_and_support'">
            <div class="form-control mt-3">
                <label>Do NOT send any communications to candidates (Recruiters will do the application process for candidates)</label>
                <div>
                    <div class="d-flex align-items-center yes-no-item">
                        <input
                            type="radio"
                            name="notification_disabled"
                            value="1"
                            :checked="(personSpec.notification_disabled == 1)"
                        />

                        <label>
                            Yes
                        </label>
                    </div>

                    <div class="d-flex align-items-center yes-no-item">
                        <input
                            type="radio"
                            name="notification_disabled"
                            value="0"
                            :checked="(personSpec.notification_disabled == 0)"
                        />

                        <label>
                            No
                        </label>
                    </div>
                </div>
            </div>
        </div>
        <div v-else>
            <input type="hidden" value="0" name="notification_disabled">
        </div>

        <input type="hidden" name="_csrfToken" :value="csrfToken" />

        <template v-if="isUsedByRequisition">
            <p>You cannot modify this person specification because it is already used by a requisition. You can clone it instead and create a new version.</p>

            <button type="submit" class="button primary" name="clone" value="1">
                Clone
            </button>
        </template>
        <template v-else>
            <button type="submit" class="button btn-green">
                Save  
            </button>

            <button type="submit" class="button primary" name="save_progress" value="1">
                Save Progress
            </button>
        </template>

        <a href="/admin/person-specifications" class="button btn-link">
            Cancel
        </a>
    </form>
</template>

<style lang="scss">
.person-spec-crud {

    .errors {
        border: 1px solid lightgray;
        padding: 1rem;
    }

    fieldset {
        margin-bottom: 1rem;

        &.bordered {
            border: 1px solid lightgray;
            padding: 1rem;
        }

        legend {
            text-transform: capitalize;
        }
    }

    .personspec-dropdown{
        background-color: white;
        max-height: 40px;
        border:solid 1px var(--vs-colors--lightest);
        font-size: 12px;

        .v__search {
            padding: 0 2px !important;
        }

        .vs__selected-options {
            display: flex;
            flex-basis: 100%;
            flex-grow: 1;
            flex-wrap: wrap;
            padding: 0 2px;
            position: relative;
            height: 40px;
            margin-top: -6px;

            .vs__selected{
                margin-top: 10px;
                height: 80%;
                font-size: 12px;
            }
        }
    }
}
</style>